export const MILORA_SITE_TITLE = 'Milora | Connect & Grow';

const DESCRIPTION_LIMIT = 200;

export type EventShareSource = {
  title: string;
  description?: string | null;
  image?: string | null;
  slug: string;
};

export type EventShareMeta = {
  title: string;
  description: string;
  image: string | null;
  url: string;
};

export type HeadMeta = {
  attr: 'name' | 'property';
  key: string;
  content: string;
};

export const SHARE_IMAGE_META_KEYS: Array<Pick<HeadMeta, 'attr' | 'key'>> = [
  { attr: 'property', key: 'og:image' },
  { attr: 'property', key: 'og:image:alt' },
  { attr: 'property', key: 'og:image:secure_url' },
  { attr: 'property', key: 'og:image:type' },
  { attr: 'property', key: 'og:image:width' },
  { attr: 'property', key: 'og:image:height' },
  { attr: 'name', key: 'twitter:image' },
  { attr: 'name', key: 'twitter:image:alt' },
];

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function characterFromCodePoint(parsed: number): string {
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 0x10ffff) return '';
  if (parsed >= 0xd800 && parsed <= 0xdfff) return '';
  return String.fromCodePoint(parsed);
}

function decodeHtmlEntities(value: string): string {
  return value
    .replace(/&#(\d+);/g, (_, code: string) => characterFromCodePoint(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, code: string) =>
      characterFromCodePoint(Number.parseInt(code, 16))
    )
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'");
}

export function plainDescription(description: string | null | undefined, title: string): string {
  const plain = decodeHtmlEntities((description ?? '').replace(/<[^>]*>/g, ' '))
    .replace(/\s+/g, ' ')
    .trim();
  const text = plain || `You're invited to ${title.trim() || 'this event'}.`;
  if (text.length <= DESCRIPTION_LIMIT) return text;
  const sliced = text.slice(0, DESCRIPTION_LIMIT - 1);
  const lastSpace = sliced.lastIndexOf(' ');
  const base = lastSpace > DESCRIPTION_LIMIT * 0.6 ? sliced.slice(0, lastSpace) : sliced;
  return `${base.trimEnd()}…`;
}

function absoluteImageUrl(image: string | null | undefined, origin: string): string | null {
  const trimmed = image?.trim() ?? '';
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('/') && origin) return `${origin.replace(/\/$/, '')}${trimmed}`;
  return null;
}

function imageContentType(url: string): string | null {
  const path = url.split('?')[0]?.toLowerCase() ?? '';
  if (path.endsWith('.png')) return 'image/png';
  if (path.endsWith('.webp')) return 'image/webp';
  if (path.endsWith('.gif')) return 'image/gif';
  if (path.endsWith('.jpg') || path.endsWith('.jpeg')) return 'image/jpeg';
  return null;
}

function eventPathSegment(slug: string): string {
  return /^[\w-]+$/.test(slug) ? slug : encodeURIComponent(slug);
}

export function eventIdFromPathname(pathname: string): string | null {
  const path = pathname.split('?')[0]?.replace(/\/+$/, '') ?? '';
  const parts = path.split('/').filter(Boolean);
  let id: string | undefined;
  if (parts.length === 2 && parts[0] === 'e') id = parts[1];
  else if (parts.length === 3 && parts[1] === 'events' && parts[0] !== 'api') id = parts[2];
  if (!id) return null;
  try {
    return decodeURIComponent(id);
  } catch {
    return id;
  }
}

export function buildEventShareMeta(
  event: EventShareSource,
  origin: string,
  pageUrl?: string
): EventShareMeta {
  const title = event.title.trim() || 'Milora';
  const root = origin.replace(/\/$/, '');
  return {
    title,
    description: plainDescription(event.description, title),
    image: absoluteImageUrl(event.image, root),
    url: pageUrl || `${root}/e/${eventPathSegment(event.slug)}`,
  };
}

export function eventHeadMeta(meta: EventShareMeta): HeadMeta[] {
  const tags: HeadMeta[] = [
    { attr: 'name', key: 'description', content: meta.description },
    { attr: 'property', key: 'og:type', content: 'website' },
    { attr: 'property', key: 'og:site_name', content: 'Milora' },
    { attr: 'property', key: 'og:title', content: meta.title },
    { attr: 'property', key: 'og:description', content: meta.description },
    { attr: 'property', key: 'og:url', content: meta.url },
    {
      attr: 'name',
      key: 'twitter:card',
      content: meta.image ? 'summary_large_image' : 'summary',
    },
    { attr: 'name', key: 'twitter:title', content: meta.title },
    { attr: 'name', key: 'twitter:description', content: meta.description },
  ];

  if (!meta.image) return tags;

  tags.push(
    { attr: 'property', key: 'og:image', content: meta.image },
    { attr: 'property', key: 'og:image:alt', content: meta.title },
    { attr: 'name', key: 'twitter:image', content: meta.image }
  );
  if (meta.image.startsWith('https://')) {
    tags.push({ attr: 'property', key: 'og:image:secure_url', content: meta.image });
  }
  const type = imageContentType(meta.image);
  if (type) tags.push({ attr: 'property', key: 'og:image:type', content: type });

  return tags;
}

export function injectEventOpenGraph(html: string, meta: EventShareMeta): string {
  const title = escapeHtml(meta.title);
  const tags = eventHeadMeta(meta)
    .map((item) => `<meta ${item.attr}="${item.key}" content="${escapeHtml(item.content)}" />`)
    .join('\n    ');

  let next = html
    .replace(
      /<meta\s+(?:name|property)=["'](?:og:image(?::[^"']*)?|twitter:image(?::[^"']*)?)["'][^>]*>\s*/gi,
      ''
    )
    .replace(/<title>[^<]*<\/title>/i, `<title>${title}</title>`);
  if (!/<title>/i.test(next)) {
    next = next.replace(/<head([^>]*)>/i, `<head$1>\n    <title>${title}</title>`);
  }

  if (/<head[^>]*>/i.test(next)) {
    return next.replace(/<head([^>]*)>/i, `<head$1>\n    ${tags}`);
  }

  return `${tags}\n${next}`;
}

type EventDetailsResponse = {
  event?: {
    title?: string | null;
    description?: string | null;
    banner?: string | null;
    slug?: string | null;
    id?: string | null;
  };
};

export async function loadEventShareSource(
  backendUrl: string,
  eventId: string
): Promise<EventShareSource | null> {
  const base = backendUrl.replace(/\/$/, '');
  const id = eventId.trim();
  if (!base || !id) return null;

  const response = await fetch(`${base}/api/events/${encodeURIComponent(id)}`, {
    headers: { accept: 'application/json' },
    signal: AbortSignal.timeout(5000),
  });
  if (!response.ok) return null;

  const body = (await response.json()) as EventDetailsResponse;
  const event = body.event;
  const title = event?.title?.trim();
  if (!title) return null;

  const slug = event?.slug?.trim() || event?.id?.trim() || id;
  return {
    title,
    description: event?.description ?? '',
    image: event?.banner ?? null,
    slug,
  };
}
